import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings, digits } from "@/context/SettingsContext";
import { fbEvent } from "@/lib/pixel";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Truck, ShieldCheck, Lock, Copy, Check, ExternalLink, MessageCircle, ChevronRight, MapPin, Plus, Minus, Pencil, Trash2 } from "lucide-react";

const SESSION_KEY = "adiyogi_checkout_v1";
const DEFAULT_UPI_ID = "9711252903@upi";
const DEFAULT_UPI_PAYEE = "AdiYogi Handcrafts";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const readSaved = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
};

const emptyAddress = (user) => ({
  full_name: user?.name || "", phone: "", email: user?.email || "",
  line1: "", line2: "", city: "", state: "", country: "India", pincode: "",
});

export default function Checkout() {
  const { items, subtotal, clearCart, updateQty, removeFromCart } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  // Never hard-code: only the active number from Admin → Contact settings.
  const WHATSAPP = digits(settings.whatsapp_number);
  const nav = useNavigate();
  const loc = useLocation();
  const saved = readSaved();
  const [step, setStep] = useState(saved?.step || 1);
  const [placing, setPlacing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [utr, setUtr] = useState(saved?.utr || "");
  const [utrErr, setUtrErr] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [address, setAddress] = useState(saved?.address || emptyAddress(user));

  // Saved-address lookup — keyed on last-10 digits of phone. Independent of localStorage.
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showForm, setShowForm] = useState(saved?.showForm !== undefined ? !!saved.showForm : true); // default: full address form visible immediately
  const lookupPhoneRef = useRef("");

  // Meta Pixel: InitiateCheckout — fired once per visit when a non-empty cart
  // reaches the checkout page.
  const firedCheckoutRef = useRef(false);
  useEffect(() => {
    if (firedCheckoutRef.current || !items.length) return;
    firedCheckoutRef.current = true;
    fbEvent("InitiateCheckout", {
      content_ids: items.map((i) => i.product_id),
      content_type: "product",
      num_items: items.reduce((s, i) => s + i.quantity, 0),
      value: subtotal,
      currency: "INR",
    });
  }, [items, subtotal]);

  // Dynamic payment settings — admin can change UPI ID / QR without redeploy.
  const [pay, setPay] = useState({ upi_id: DEFAULT_UPI_ID, upi_name: DEFAULT_UPI_PAYEE, qr_image_url: "", is_upi_enabled: true });
  useEffect(() => {
    api.get("/payment-settings")
      .then(({ data }) => setPay({
        upi_id: data.upi_id || DEFAULT_UPI_ID,
        upi_name: data.upi_name || DEFAULT_UPI_PAYEE,
        qr_image_url: data.qr_image_url || "",
        is_upi_enabled: data.is_upi_enabled !== false,
      }))
      .catch(() => {});
  }, []);

  const couponCode = loc.state?.couponCode || null;

  // Persist checkout progress so back/forward/refresh keeps entered details.
  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ step, address, utr, showForm })); } catch {}
  }, [step, address, utr, showForm]);

  // On phone change (>=10 digits), look up saved addresses for that phone.
  useEffect(() => {
    const digits = (address.phone || "").replace(/\D/g, "");
    if (digits.length < 10) { setSavedAddresses([]); setShowForm(true); return; }
    if (lookupPhoneRef.current === digits) return;
    lookupPhoneRef.current = digits;
    api.get("/addresses", { params: { phone: digits } })
      .then(({ data }) => {
        const list = data.addresses || [];
        setSavedAddresses(list);
        setShowForm(list.length === 0); // no saved address → show the complete form immediately
      })
      .catch(() => setSavedAddresses([]));
  }, [address.phone]);

  const applySavedAddress = (a) => {
    setAddress({
      full_name: a.full_name || "",
      phone: a.phone || "",
      email: a.email || "",
      line1: a.line1 || "",
      line2: a.line2 || "",
      city: a.city || "",
      state: a.state || "",
      country: a.country || "India",
      pincode: a.pincode || "",
    });
    setShowForm(false);
  };

  const startNewAddress = () => {
    // Keep the phone so lookup stays consistent, clear the rest.
    setAddress((prev) => ({ ...emptyAddress(user), phone: prev.phone }));
    setShowForm(true);
    setSavedAddresses([]); // hide selector while user types the new one
  };

  const editCurrentAddress = () => setShowForm(true);

  const shipping = subtotal >= 1999 || subtotal === 0 ? 0 : (address.country?.toLowerCase() === "india" ? 79 : 1499);
  const total = subtotal + shipping;
  const orderRef = `ADIYOGI-${Date.now().toString().slice(-8)}`;
  const upiLink = `upi://pay?pa=${pay.upi_id}&pn=${encodeURIComponent(pay.upi_name)}&am=${total}&cu=INR&tn=${encodeURIComponent(orderRef)}`;

  const validate = () => {
    if (items.length === 0) { toast.error("Cart is empty"); return false; }
    const req = ["full_name","phone","line1","city","state","pincode"];
    for (const k of req) if (!address[k]) { toast.error("Please fill all required fields"); return false; }
    if (!/^[0-9+\-\s]{10,15}$/.test(address.phone) || address.phone.replace(/\D/g, "").length < 10) {
      toast.error("Enter a valid 10-digit mobile number"); return false;
    }
    if (address.country.toLowerCase() === "india" && !/^\d{6}$/.test(address.pincode)) {
      toast.error("Enter a valid 6-digit PIN code"); return false;
    }
    // Email is optional; only validate format when provided.
    const emailVal = (address.email || "").trim();
    if (emailVal && !EMAIL_RE.test(emailVal)) {
      setEmailErr("Enter a valid email or leave it blank");
      toast.error("Enter a valid email or leave it blank"); return false;
    }
    setEmailErr("");
    return true;
  };

  const captureAbandoned = async (attempted) => {
    // Capture checkout details for abandoned-payment recovery (Admin → Customers → Payment Abandoned).
    try {
      await api.post("/checkout/abandoned", {
        full_name: address.full_name, phone: address.phone, email: (address.email || "").trim() || null,
        address,
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image, line_total: i.price * i.quantity })),
        subtotal, shipping, total,
        payment_attempted: !!attempted,
      });
    } catch {}
  };

  const goToPay = () => { if (validate()) { captureAbandoned(false); setStep(2); window.scrollTo(0, 0); } };

  const copyUpi = async () => {
    try { await navigator.clipboard.writeText(pay.upi_id); setCopied(true); toast.success("UPI ID copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Copy failed. UPI ID: " + pay.upi_id); }
  };

  const placeOrder = async () => {
    if (placing) return;
    // UTR is required for payment confirmation.
    const utrVal = utr.trim();
    if (!utrVal || utrVal.length < 6) {
      setUtrErr("Please enter your UPI transaction / UTR reference (min 6 chars)");
      toast.error("Please enter your UPI transaction / UTR reference");
      return;
    }
    setUtrErr("");
    // Never send empty string as email — backend EmailStr will reject it.
    const cleanEmail = (address.email || "").trim();
    const cleanAddress = { ...address, email: cleanEmail || null };
    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size || null, design: i.design || null, variant_id: i.variant_id || null })),
        address: cleanAddress,
        payment_method: "upi",
        coupon_code: couponCode,
        utr_reference: utrVal,
      };
      if (!user && cleanEmail) payload.guest_email = cleanEmail;
      await captureAbandoned(true); // mark payment attempted before the order lands
      const { data } = await api.post("/checkout", payload);
      clearCart();
      try { sessionStorage.removeItem(SESSION_KEY); } catch {}
      try { sessionStorage.setItem(`adiyogi_just_paid_${data.order_number}`, "1"); } catch {}
      // Move the order to Processing right away (idempotent on the backend).
      api.post(`/orders/${data.order_number}/mark-processing`).catch(() => {});
      toast.success("Order placed — processing");
      if (data.admin_whatsapp_url && !data.admin_whatsapp_api_sent) {
        try { window.open(data.admin_whatsapp_url, "_blank"); } catch {}
      }
      nav(`/order-success/${data.order_number}?guest=${user ? 0 : 1}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container-x py-20 text-center">
        <div className="font-serif text-3xl mb-2">Your cart is empty</div>
        <p className="text-sm text-muted-foreground mb-6">Add products before checking out.</p>
        <Link to="/shop" className="btn-gold rounded-full px-8 py-3 text-xs uppercase tracking-widest">Start Shopping</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-8 pb-28 md:py-10" data-testid="checkout-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-5xl">Checkout</h1>
          <p className="text-sm text-muted-foreground mt-1">Guest checkout · Prepaid orders only · UPI payment</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step>=1 ? "bg-[#B8860B] text-ivory" : "border hairline"}`}>1</div>
          <span className={step===1 ? "font-medium" : "text-muted-foreground"}>Details</span>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${step>=2 ? "bg-[#B8860B] text-ivory" : "border hairline"}`}>2</div>
          <span className={step===2 ? "font-medium" : "text-muted-foreground"}>UPI Payment</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6 md:gap-10">
        <div className="space-y-6">
          {step === 1 && (
            <section className="border hairline rounded-xl p-5 md:p-6 bg-white" data-testid="checkout-step-1">
              <div className="flex items-center gap-2 mb-4"><Truck className="w-4 h-4 gold-text" strokeWidth={1.5} /><div className="font-serif text-xl">Delivery details</div></div>

              {/* Saved addresses selector */}
              {savedAddresses.length > 0 && !showForm && (
                <div className="mb-5 space-y-3" data-testid="saved-addresses-block">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Delivery Address</div>
                  {savedAddresses.map((a, idx) => {
                    const active = address.line1 === a.line1 && address.pincode === a.pincode;
                    return (
                      <button
                        key={a.address_id || idx}
                        type="button"
                        onClick={() => applySavedAddress(a)}
                        className={`w-full text-left border rounded-xl p-4 transition ${active ? "border-[#B8860B] bg-[#B8860B]/5" : "hairline hover:border-[#B8860B]"}`}
                        data-testid={`saved-address-${idx}`}
                      >
                        <div className="flex items-start gap-3">
                          <MapPin className={`w-4 h-4 mt-0.5 ${active ? "gold-text" : "text-muted-foreground"}`} strokeWidth={1.5} />
                          <div className="text-sm flex-1">
                            <div className="font-medium">{a.full_name}</div>
                            <div className="text-muted-foreground">{a.phone}</div>
                            <div className="mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                            <div className="text-muted-foreground">{a.city}, {a.state} - {a.pincode}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button onClick={goToPay} className="btn-gold rounded-full px-6 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="use-saved-address-btn">
                      <Check className="w-3.5 h-3.5" strokeWidth={1.5} /> Use This Address
                    </button>
                    <button onClick={editCurrentAddress} className="btn-outline-gold rounded-full px-5 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2" data-testid="edit-address-btn">
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} /> Edit Address
                    </button>
                    <button onClick={startNewAddress} className="rounded-full px-5 py-2.5 text-xs uppercase tracking-widest border hairline hover:border-[#B8860B] inline-flex items-center gap-2" data-testid="add-new-address-btn">
                      <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add New Address
                    </button>
                  </div>
                </div>
              )}

              {/* Full delivery form (new customer OR add-new/edit) */}
              {showForm && (
                <>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    {[
                      ["full_name", "Full Name *", "text", { autoComplete: "name" }],
                      ["phone", "Mobile Number *", "tel", { inputMode: "tel", autoComplete: "tel" }],
                      ["line1", "Address (House No, Street, Area) *", "text", { autoComplete: "address-line1" }],
                      ["line2", "Landmark / Locality (optional)", "text", { autoComplete: "address-line2" }],
                      ["city", "City *", "text", { autoComplete: "address-level2" }],
                      ["state", "State *", "text", { autoComplete: "address-level1" }],
                      ["pincode", "PIN Code / ZIP Code *", "text", { inputMode: "numeric", autoComplete: "postal-code" }],
                      ["country", "Country *", "text", { autoComplete: "country-name" }],
                      ["email", "Email (optional)", "email", { inputMode: "email", autoComplete: "email" }],
                    ].map(([k, label, type, attrs]) => (
                      <label key={k} className={`block ${k === "line1" || k === "line2" ? "md:col-span-2" : ""}`}>
                        <div className="text-xs text-muted-foreground mb-1">{label}</div>
                        <input data-testid={`addr-${k}`} type={type} {...attrs} value={address[k]} onChange={(e) => setAddress({ ...address, [k]: e.target.value })}
                          className="w-full border hairline rounded-lg px-3 py-3 text-base md:text-sm bg-white outline-none focus:border-[#B8860B]" />
                      </label>
                    ))}
                  </div>
                  {emailErr && <div className="text-xs text-destructive mt-1" data-testid="email-error">{emailErr}</div>}
                  <div className="mt-3 text-[11px] text-muted-foreground text-center">Prepaid payment only. No cash on delivery.</div>
                </>
              )}
            </section>
          )}

          {step === 2 && (
            <section className="border hairline rounded-xl p-5 md:p-6 bg-white">
              <div className="flex items-center gap-2 mb-2"><Lock className="w-4 h-4 gold-text" strokeWidth={1.5} /><div className="font-serif text-xl">Pay using UPI</div></div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Prepaid Payment Only</div>
              <p className="text-sm text-muted-foreground mb-3" data-testid="upi-instruction">
                Scan the QR code or use the UPI ID to complete your payment. Once done, tap <span className="text-espresso font-medium">“I Have Paid”</span> below and we&apos;ll verify &amp; confirm your order shortly.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-5 text-[11px]" data-testid="payment-trust-lines">
                <span className="inline-flex items-center gap-1.5 text-espresso font-medium"><Lock className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} /> Secure UPI Payment</span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground"><ShieldCheck className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} /> Prepaid orders only</span>
                <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Check className="w-3.5 h-3.5 gold-text" strokeWidth={1.5} /> Order confirmed after payment verification</span>
              </div>

              <div className="grid md:grid-cols-[220px_1fr] gap-5 md:gap-6 items-start">
                <div className="flex flex-col items-center gap-2" data-testid="upi-qr-block">
                  <div className="bg-white p-3 border hairline rounded-xl">
                    <QRCodeSVG value={upiLink} size={192} level="M" includeMargin={false} fgColor="#2C241E" bgColor="#FDFBF7" />
                  </div>
                  <div className="text-[11px] text-center text-emerald-700 font-medium" data-testid="qr-amount-hint">Amount ₹{total} auto-filled when scanned</div>
                  <div className="text-[11px] text-muted-foreground text-center">Scan with any UPI app<br/>PhonePe · GPay · Paytm · BHIM</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">UPI ID</div>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <div className="font-serif text-xl md:text-2xl gold-text break-all" data-testid="upi-id-display">{pay.upi_id}</div>
                    <button onClick={copyUpi} className="btn-outline-gold rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest flex items-center gap-1.5 shrink-0" data-testid="copy-upi-btn">
                      {copied ? <><Check className="w-3 h-3" strokeWidth={1.5} /> Copied</> : <><Copy className="w-3 h-3" strokeWidth={1.5} /> Copy</>}
                    </button>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">Payee</div>
                  <div className="text-sm">{pay.upi_name}</div>
                  <div className="mt-4 rounded-lg bg-[#B8860B]/10 border border-[#B8860B]/30 px-4 py-3">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-espresso/70">Amount to Pay</div>
                    <div className="font-serif text-3xl gold-text leading-none mt-1" data-testid="upi-amount">₹{total}</div>
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">Reference</div>
                  <div className="text-sm font-mono">{orderRef}</div>

                  <a href={upiLink} className="mt-4 block btn-gold rounded-full py-3 text-xs uppercase tracking-widest text-center md:hidden" data-testid="upi-app-link">
                    Open UPI App to Pay ₹{total}
                  </a>
                </div>
              </div>

              <div className="mt-6 border-t hairline pt-5">
                <div className="font-medium text-sm mb-1">UTR / Transaction ID <span className="text-rose-600 font-normal">(Required)</span></div>
                <input value={utr} onChange={(e)=>{setUtr(e.target.value); setUtrErr("");}} placeholder="e.g. 402312345678" className={`w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#B8860B] ${utrErr ? "border-destructive" : "hairline"}`} data-testid="utr-input" />
                {utrErr && <div className="text-xs text-destructive mt-1" data-testid="utr-error">{utrErr}</div>}
                <div className="text-[11px] text-muted-foreground mt-2">
                  Your UTR (usually a 12-digit number shown on the payment-success screen of your UPI app) helps us verify your payment quickly. Please enter it before tapping <span className="text-espresso font-medium">"I Have Paid"</span>.
                </div>

                <button onClick={placeOrder} disabled={placing} className="mt-5 w-full btn-gold rounded-full py-3.5 text-xs uppercase tracking-widest" data-testid="place-order-btn">
                  {placing ? "Submitting…" : "I Have Paid"}
                </button>
                <button onClick={() => setStep(1)} className="mt-3 block w-full text-xs text-muted-foreground hover:gold-text">← Edit delivery details</button>

                <div className="mt-5 flex items-center justify-between text-xs border-t hairline pt-4">
                  <div className="flex items-center gap-2 text-muted-foreground"><MessageCircle className="w-4 h-4 text-emerald-600" strokeWidth={1.5} /> Need help paying?</div>
                  {WHATSAPP && (
                    <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent("Hello AdiYogi Handcrafts, I need help with my UPI payment for order " + orderRef)}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline flex items-center gap-1" data-testid="wa-payment-help">
                      Chat on WhatsApp <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                    </a>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        <aside className="border hairline rounded-xl p-4 md:p-6 bg-white h-fit lg:sticky lg:top-40" data-testid="order-summary">
          <div className="font-serif text-xl md:text-2xl mb-4">Order Summary</div>
          <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
            {items.map((it) => (
              <div key={it.line_id} className="flex items-start gap-3 text-sm border hairline rounded-lg p-2.5" data-testid={`summary-item-${it.product_id}`}>
                <img src={it.image} className="w-14 h-14 rounded object-cover" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm leading-snug">{it.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    ₹{it.price}{it.unit_label ? <span> / {it.unit_label.replace(/^per\s+/i,'').toLowerCase()}</span> : null}
                    {it.size ? <> · Size <span className="text-espresso">{it.size}</span></> : null}
                    {it.design ? <> · <span className="text-espresso">{it.design}</span></> : null}
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="inline-flex items-center border hairline rounded-full bg-white">
                      <button type="button" onClick={() => updateQty(it.line_id, it.quantity - 1)} className="w-8 h-8 flex items-center justify-center" aria-label="Decrease quantity" data-testid={`qty-minus-${it.product_id}`}><Minus className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                      <span className="w-7 text-center text-sm font-medium" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                      <button type="button" onClick={() => updateQty(it.line_id, it.quantity + 1)} className="w-8 h-8 flex items-center justify-center" aria-label="Increase quantity" data-testid={`qty-plus-${it.product_id}`}><Plus className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                    </div>
                    <button type="button" onClick={() => removeFromCart(it.line_id)} className="text-muted-foreground hover:text-destructive p-1.5" aria-label="Remove item" data-testid={`remove-${it.product_id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                  </div>
                </div>
                <div className="font-medium whitespace-nowrap" data-testid={`line-total-${it.product_id}`}>₹{it.price * it.quantity}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t hairline pt-3">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">₹{subtotal}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span data-testid="checkout-shipping">{shipping ? `₹${shipping}` : "Free"}</span></div>
            <div className="border-t hairline pt-3 flex justify-between items-center">
              <span className="font-medium uppercase tracking-widest text-xs">Grand Total</span>
              <span className="font-serif text-2xl md:text-3xl gold-text" data-testid="checkout-total">₹{total}</span>
            </div>
          </div>
          {step === 1 && (
            <button onClick={goToPay} className="mt-5 w-full btn-gold rounded-full py-4 text-xs md:text-sm uppercase tracking-widest" data-testid="continue-to-pay">
              Proceed to UPI Payment →
            </button>
          )}
          <div className="mt-4 border hairline rounded-lg p-3 bg-muted/40 text-xs space-y-1.5">
            <div className="flex items-center gap-2 text-espresso font-medium"><Lock className="w-4 h-4 gold-text" strokeWidth={1.5} /> Secure UPI Payment</div>
            <div className="flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-4 h-4 gold-text" strokeWidth={1.5} /> Prepaid orders only</div>
            <div className="flex items-center gap-2 text-muted-foreground"><Check className="w-4 h-4 gold-text" strokeWidth={1.5} /> Order confirmed after payment verification</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
